/**
 * InventoryBalanceTable — Sortable, searchable table of inventory items grouped by item.
 * Displays ARG-style inventory balance data: Item ID, Display Name, On Hand, On Hand Value.
 * Rows expand to show per-location/lot detail.
 */

import { useState, useMemo } from "react";
import { GroupedItem } from "../pages/Inventory";
import { formatNumber, formatCurrency } from "../utils/format";

interface InventoryBalanceTableProps {
  groups: GroupedItem[];
}

type SortField =
  | "itemid"
  | "displayname"
  | "totalOnHand"
  | "totalOnHandValue"
  | "avgDailyUnits90d"
  | "daysOnHand";
type SortDir = "asc" | "desc";

function SortIcon({
  field,
  currentField,
  currentDir,
}: {
  field: SortField;
  currentField: SortField;
  currentDir: SortDir;
}) {
  if (field !== currentField) {
    return (
      <svg
        className="w-3 h-3 text-gray-300 ml-1 inline"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path d="M5 8l5-5 5 5H5zm0 4l5 5 5-5H5z" />
      </svg>
    );
  }
  return (
    <svg
      className="w-3 h-3 text-wmp-500 ml-1 inline"
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      {currentDir === "asc" ? (
        <path d="M5 12l5-5 5 5H5z" />
      ) : (
        <path d="M5 8l5 5 5-5H5z" />
      )}
    </svg>
  );
}

export function InventoryBalanceTable({ groups }: InventoryBalanceTableProps) {
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("itemid");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [expandedItem, setExpandedItem] = useState<string | null>(null);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDir(
        field === "itemid" || field === "displayname" ? "asc" : "desc",
      );
    }
  };

  const filteredAndSorted = useMemo(() => {
    let result = groups;

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (g) =>
          g.itemid.toLowerCase().includes(q) ||
          g.displayname.toLowerCase().includes(q),
      );
    }

    result = [...result].sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];

      // Nulls sort last regardless of direction
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      if (typeof aVal === "number" && typeof bVal === "number") {
        return sortDir === "asc" ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal).toLowerCase();
      const bStr = String(bVal).toLowerCase();
      return sortDir === "asc"
        ? aStr.localeCompare(bStr)
        : bStr.localeCompare(aStr);
    });

    return result;
  }, [groups, search, sortField, sortDir]);

  const totalValue = useMemo(
    () => groups.reduce((sum, g) => sum + g.totalOnHandValue, 0),
    [groups],
  );

  const totalOnHand = useMemo(
    () => groups.reduce((sum, g) => sum + g.totalOnHand, 0),
    [groups],
  );

  const thClass =
    "text-left text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 transition-colors select-none whitespace-nowrap";
  const thRightClass =
    "text-right text-2xs font-bold tracking-wider text-gray-500 uppercase px-4 py-2.5 cursor-pointer hover:text-gray-700 transition-colors select-none whitespace-nowrap";

  return (
    <div className="bg-white rounded-xl border border-gray-200/70 shadow-card overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2.5">
            <h2 className="text-lg font-semibold text-gray-900">
              Inventory by Item
            </h2>
            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-medium">
              {filteredAndSorted.length.toLocaleString()}
              {search && ` / ${groups.length.toLocaleString()}`}
            </span>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <span className="text-xs text-gray-400 block">
                Total On Hand Value
              </span>
              <span className="text-sm font-bold text-teal-600 tabular-nums">
                {formatCurrency(totalValue)}
              </span>
            </div>
            <div className="text-right">
              <span className="text-xs text-gray-400 block">Total On Hand</span>
              <span className="text-sm font-bold text-gray-700 tabular-nums">
                {formatNumber(totalOnHand)}
              </span>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <svg
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
          <input
            type="text"
            placeholder="Search by item ID or name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200/70 rounded-lg focus:outline-none focus:ring-2 focus:ring-wmp-500/40 focus:border-wmp-500 transition-colors"
          />
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-wmp-50/70 border-b border-gray-200">
              <th className="w-8 px-2 py-2.5" />
              <th className={thClass} onClick={() => handleSort("itemid")}>
                Item ID
                <SortIcon
                  field="itemid"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th className={thClass} onClick={() => handleSort("displayname")}>
                Display Name
                <SortIcon
                  field="displayname"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th
                className={thRightClass}
                onClick={() => handleSort("totalOnHand")}
              >
                On Hand
                <SortIcon
                  field="totalOnHand"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th
                className={thRightClass}
                onClick={() => handleSort("totalOnHandValue")}
              >
                On Hand Value
                <SortIcon
                  field="totalOnHandValue"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th
                className={thRightClass}
                onClick={() => handleSort("avgDailyUnits90d")}
              >
                Avg Daily (90d)
                <SortIcon
                  field="avgDailyUnits90d"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
              <th
                className={thRightClass}
                onClick={() => handleSort("daysOnHand")}
              >
                Days on Hand
                <SortIcon
                  field="daysOnHand"
                  currentField={sortField}
                  currentDir={sortDir}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSorted.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-6 py-10 text-center text-sm text-gray-400"
                >
                  {search
                    ? "No items match your search."
                    : "No inventory items found."}
                </td>
              </tr>
            ) : (
              filteredAndSorted.map((group) => {
                const isExpanded = expandedItem === group.itemid;
                return (
                  <ItemRow
                    key={group.itemid}
                    group={group}
                    isExpanded={isExpanded}
                    onToggle={() =>
                      setExpandedItem(isExpanded ? null : group.itemid)
                    }
                  />
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ItemRow({
  group,
  isExpanded,
  onToggle,
}: {
  group: GroupedItem;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className="border-b border-gray-50 last:border-b-0 hover:bg-wmp-50/50 transition-colors cursor-pointer"
        onClick={onToggle}
      >
        <td className="w-8 px-2 py-2.5 text-center">
          <svg
            className={`w-3.5 h-3.5 text-gray-400 transition-transform ${isExpanded ? "rotate-90" : ""}`}
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path d="M6 4l8 6-8 6V4z" />
          </svg>
        </td>
        <td className="px-4 py-2.5 text-sm font-mono text-gray-700 whitespace-nowrap">
          {group.itemid}
        </td>
        <td className="px-4 py-2.5 text-sm text-gray-900 max-w-xs truncate">
          {group.displayname || "—"}
        </td>
        <td className="px-4 py-2.5 text-sm text-gray-700 text-right tabular-nums">
          {formatNumber(group.totalOnHand)}
        </td>
        <td className="px-4 py-2.5 text-sm font-medium text-teal-700 text-right tabular-nums">
          {formatCurrency(group.totalOnHandValue)}
        </td>
        <td className="px-4 py-2.5 text-sm text-gray-700 text-right tabular-nums">
          {group.avgDailyUnits90d != null
            ? group.avgDailyUnits90d.toLocaleString("en-US", {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })
            : "—"}
        </td>
        <td className="px-4 py-2.5 text-sm text-right tabular-nums">
          {group.daysOnHand != null ? (
            <span
              className={
                group.daysOnHand <= 30
                  ? "text-red-600 font-medium"
                  : group.daysOnHand <= 90
                    ? "text-amber-600 font-medium"
                    : "text-gray-700"
              }
            >
              {formatNumber(group.daysOnHand)}
            </span>
          ) : (
            "—"
          )}
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={7} className="px-0 py-0">
            <div className="bg-gray-50/60 border-y border-gray-100">
              <table className="w-full border-collapse">
                <thead>
                  <tr>
                    <th className="microlabel text-left px-4 py-2 pl-12">
                      Location
                    </th>
                    <th className="microlabel text-left px-4 py-2">
                      Inventory #
                    </th>
                    <th className="microlabel text-left px-4 py-2">Status</th>
                    <th className="microlabel text-left px-4 py-2">
                      Expiration
                    </th>
                    <th className="microlabel text-right px-4 py-2">On Hand</th>
                    <th className="microlabel text-right px-4 py-2">
                      Available
                    </th>
                    <th className="microlabel text-right px-4 py-2">
                      On Hand Value
                    </th>
                    <th className="microlabel text-right px-4 py-2">
                      Days on Hand
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {group.rows.map((row, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-gray-100/60 last:border-b-0"
                    >
                      <td className="px-4 py-1.5 pl-12 text-xs text-gray-600">
                        {String(row.locationname ?? "—")}
                      </td>
                      <td className="px-4 py-1.5 text-xs font-mono text-gray-600">
                        {String(row.inventorynumber ?? "—")}
                      </td>
                      <td className="px-4 py-1.5 text-xs text-gray-500">
                        {String(row.status ?? "—")}
                      </td>
                      <td className="px-4 py-1.5 text-xs text-gray-500">
                        {row.expirationdate
                          ? String(row.expirationdate).split("T")[0]
                          : "—"}
                      </td>
                      <td className="px-4 py-1.5 text-xs text-gray-600 text-right tabular-nums">
                        {formatNumber(Number(row.quantityonhand) || 0)}
                      </td>
                      <td className="px-4 py-1.5 text-xs text-gray-600 text-right tabular-nums">
                        {formatNumber(Number(row.quantityavailable) || 0)}
                      </td>
                      <td className="px-4 py-1.5 text-xs font-medium text-teal-700 text-right tabular-nums">
                        {formatCurrency(Number(row.onhandvalue) || 0)}
                      </td>
                      <td className="px-4 py-1.5 text-xs text-right tabular-nums">
                        {row.days_on_hand != null ? (
                          <span
                            className={
                              Number(row.days_on_hand) <= 30
                                ? "text-red-600"
                                : Number(row.days_on_hand) <= 90
                                  ? "text-amber-600"
                                  : "text-gray-500"
                            }
                          >
                            {formatNumber(Number(row.days_on_hand))}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
